/**
 * ColumnPicker — lets users choose visible columns, reorder, and set widths.
 * Opens as a popover from a gear icon. Persists preferences to backend.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Settings2, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import api from '../../services/api';

export default function ColumnPicker({ module, reportId, formatValue, defaultColumns, onColumnsChange }) {
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const prefKey = `${module}_${reportId}${formatValue ? `_${formatValue}` : ''}`;

  // Initialize from defaults
  useEffect(() => {
    const mapped = defaultColumns.map(c => ({ ...c, visible: true, width: c.width || null }));
    setCols(mapped);
    // Load saved prefs
    api.get(`/api/saved-reports/column-prefs/${module}/${prefKey}`).then(r => {
      if (r.data?.columns?.length) {
        // Merge saved prefs with defaults (in case new columns were added)
        const savedMap = {};
        r.data.columns.forEach((sc, i) => { savedMap[sc.key] = { ...sc, _order: i }; });
        const merged = [];
        // First add saved columns in saved order
        const savedKeys = r.data.columns.map(sc => sc.key);
        for (const sk of savedKeys) {
          const def = defaultColumns.find(d => d.key === sk);
          if (def) {
            const saved = savedMap[sk];
            merged.push({ ...def, visible: saved.visible !== false, width: saved.width || def.width || null });
          }
        }
        // Then add any new columns not in saved prefs
        for (const dc of defaultColumns) {
          if (!savedKeys.includes(dc.key)) {
            merged.push({ ...dc, visible: true, width: dc.width || null });
          }
        }
        setCols(merged);
        onColumnsChange(merged.filter(c => c.visible));
      } else {
        onColumnsChange(mapped);
      }
    }).catch(() => {
      onColumnsChange(mapped);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefKey]);

  const save = useCallback((updated) => {
    const toSave = updated.map(c => ({ key: c.key, visible: c.visible, width: c.width }));
    api.put(`/api/saved-reports/column-prefs/${module}/${prefKey}`, { columns: toSave }).catch(() => {});
    onColumnsChange(updated.filter(c => c.visible));
  }, [module, prefKey, onColumnsChange]);

  const toggleCol = (idx) => {
    const updated = [...cols];
    updated[idx] = { ...updated[idx], visible: !updated[idx].visible };
    setCols(updated);
    save(updated);
  };

  const setWidth = (idx, w) => {
    const updated = [...cols];
    updated[idx] = { ...updated[idx], width: w || null };
    setCols(updated);
    save(updated);
  };

  const resetDefaults = () => {
    const mapped = defaultColumns.map(c => ({ ...c, visible: true, width: c.width || null }));
    setCols(mapped);
    save(mapped);
  };

  const handleDragStart = (idx) => { setDragIdx(idx); };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const updated = [...cols];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    setCols(updated);
    setDragIdx(idx);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    save(cols);
  };

  if (!defaultColumns?.length) return null;

  return (
    <div className="relative">
      <Button size="sm" variant="outline" onClick={() => setOpen(!open)}
        className="h-7 px-2" data-testid="column-picker-btn" title="Configure columns">
        <Settings2 className="h-3.5 w-3.5" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-50 w-72 bg-white border rounded-xl shadow-xl p-3 max-h-[400px] overflow-auto"
            data-testid="column-picker-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800">Configure Columns</span>
              <button onClick={resetDefaults} className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                data-testid="column-picker-reset">
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mb-2">Drag to reorder. Toggle visibility. Set width (px).</p>
            <div className="space-y-1">
              {cols.map((c, i) => (
                <div key={c.key}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs transition-colors ${
                    c.visible ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
                  } ${dragIdx === i ? 'ring-2 ring-primary/30' : ''}`}
                  data-testid={`column-picker-item-${c.key}`}>
                  <GripVertical className="h-3 w-3 text-slate-400 cursor-grab flex-shrink-0" />
                  <button onClick={() => toggleCol(i)} className="flex-shrink-0" data-testid={`column-toggle-${c.key}`}>
                    {c.visible
                      ? <Eye className="h-3.5 w-3.5 text-emerald-600" />
                      : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                  </button>
                  <span className={`flex-1 truncate ${c.visible ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{c.label}</span>
                  <input type="number" placeholder="auto" min="40" max="500" step="10"
                    value={c.width || ''}
                    onChange={e => setWidth(i, e.target.value ? parseInt(e.target.value) : null)}
                    className="w-14 h-5 text-[10px] border rounded px-1 text-right"
                    title="Width (px)"
                    data-testid={`column-width-${c.key}`} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
